/**
 * La clave numérica de Hacienda, traída de Business Central.
 *
 * Son los 50 dígitos que Costa Rica imprime en el comprobante. Se componen de
 * seis tramos —país, fecha, cédula del emisor, consecutivo, situación y un
 * **código de seguridad de ocho dígitos que asigna quien emite**—, y ese último
 * tramo el terminal no lo tiene ni lo puede calcular.
 *
 * Por eso el terminal dejó de componerla. Lo hacía en `claveNumerica.js`, y esa
 * composición **nunca podía salir bien**: sin el código de seguridad devolvía la
 * marca de pendiente, y con uno inventado habría impreso una clave que parece
 * válida y no lo es, que es peor. Ahora se pide la que CertificaE ya escribió.
 *
 * Acuerdo `contrato-tipo-documento-electronico.md`, ronda 8: se sirve por
 * `Importaciones_estadoFactura`, que acepta el número del documento registrado
 * además de la terna, y devuelve `clave` —vacía mientras no se haya timbrado—.
 */
import db from "../db/database";
import { consultarEstadoFactura } from "./estadoFacturaBCService";
import { ESTADO_DEL_TIMBRADO, leerElTimbrado } from "./estadoDelTimbrado";
import { leerLaRespuestaDelEmisor } from "./respuestaDelEmisor";
import { crearRegistro } from "../utils/registro";

const registro = crearRegistro("factura");

/** Un documento sin número registrado todavía no puede tener clave. */
const numeroRegistradoDe = (invoice) => String(invoice?.No ?? "").trim();

/**
 * El timbrado de un documento: de la fila local si ya está, y si no de BC.
 *
 * No es un error que no haya clave: mientras CertificaE no timbre el documento,
 * no existe en ninguna parte. Lo que cambió es que ahora **también se guarda por
 * qué**: la misma respuesta trae el estado y, cuando lo hay, el motivo escrito
 * para leerse. Antes se descartaban los dos en la misma línea en que llegaban y
 * el cajero se quedaba mirando un hueco en blanco sin saber si esperar.
 *
 * @param {object} invoice La fila local del documento.
 * @returns {Promise<{estado: string, clave: string, referencia: string, motivo: string}>}
 */
export const traerElTimbrado = async (invoice) => {
  const yaGuardada = String(invoice?.Clave ?? "").trim();
  // Con la clave ya en la fila no hace falta preguntar: no cambia una vez
  // asignada, y tenerla ya prueba el timbrado.
  if (yaGuardada) {
    return {
      estado: ESTADO_DEL_TIMBRADO.TIMBRADO,
      clave: yaGuardada,
      referencia: String(invoice?.Electronic_Document_Status ?? "").trim(),
      motivo: "",
    };
  }

  const no = numeroRegistradoDe(invoice);
  // Sin número registrado no hay nada que preguntar todavía. No es un fallo, así
  // que no se devuelve «desconocido»: es que aún no le toca.
  if (!no) {
    return { estado: ESTADO_DEL_TIMBRADO.EN_CAMINO, clave: "", referencia: "", motivo: "" };
  }

  const respuesta = await consultarEstadoFactura({
    no,
    documentType: invoice?.Document_Type || "Invoice",
  });

  const timbrado = leerElTimbrado(respuesta);

  // No se pudo preguntar: no se escribe nada. Guardar «desconocido» encima de un
  // estado bueno sería empeorar la fila por haber tenido mala red.
  if (timbrado.estado === ESTADO_DEL_TIMBRADO.DESCONOCIDO || !invoice?.id) return timbrado;

  // Se guarda para no volver a preguntar en cada reimpresión, y para que el
  // listado pueda decir de un vistazo qué documentos no timbraron.
  //
  // Los dos campos existían en el esquema desde siempre y **nadie los escribía
  // con un valor**: sólo se ponían a cadena vacía al crear un abono. El nombre
  // coincide con un campo de Business Central que NO es éste --el estándar
  // `Electronic_Document_Status` de `PostedSalesInvoice` sale en blanco en el
  // 100 % de los 595 documentos medidos--; el que vale es el de esta respuesta.
  // Lo que contestó el emisor viaja en la MISMA respuesta, así que se guarda en
  // el mismo `update`: ni una petición más ni una escritura extra.
  //
  // Y **nunca en `Mensaje`**, que es la ranura de `motivoElectronico` y la
  // entrada del distintivo rojo del listado: meterlo ahí pintaría «Sin timbrar»
  // cada documento que traiga un mensaje del emisor, incluidos los aceptados.
  // Acuerdo `contrato-tipo-documento-electronico.md`, ronda 10.
  const delEmisor = leerLaRespuestaDelEmisor(respuesta);

  try {
    await db.invoices.update(invoice.id, {
      ...(timbrado.clave ? { Clave: timbrado.clave } : {}),
      Electronic_Document_Status: timbrado.referencia,
      Mensaje: timbrado.motivo,
      MensajeElectronico: delEmisor.texto,
      CodigoElectronico: delEmisor.codigo,
      MensajeElectronicoCompleto: delEmisor.completo,
      RespuestaEmisorLeidaEn: new Date().toISOString(),
    });
  } catch (error) {
    // Que no se pueda guardar no impide imprimir lo que ya se sabe.
    registro.warn(`No se pudo guardar el timbrado de ${no}: ${error.message}`);
  }

  return timbrado;
};

/**
 * Sólo la clave, para quien no necesita el resto.
 *
 * @param {object} invoice La fila local del documento.
 * @returns {Promise<string>} Los 50 dígitos, o `""`.
 */
export const traerClaveDeHacienda = async (invoice) => (await traerElTimbrado(invoice)).clave;
